/**
 * Rate limits for outbound invite emails (resend / copy-link flow).
 *
 * Pure helpers + types + constants only — no Convex function registration, so
 * this module is safe to import from the browser. The internal mutation that
 * persists the counters lives in `convex/invite_email_rate_limit.ts`.
 */
import { ConvexError } from "convex/values";

/** No per-send cooldown — admins may invite and immediately resend (typo fix). */
export const INVITE_EMAIL_RESEND_COOLDOWN_MS = 0;
/** High daily cap — only blocks runaway loops / abuse, not normal onboarding. */
export const INVITE_EMAIL_RESEND_MAX_PER_24H = 60;
const WINDOW_MS = 24 * 60 * 60 * 1000;

export const INVITE_EMAIL_RATE_LIMIT_CODE = "invite_rate_limit" as const;

export type InviteEmailRateLimitConvexData = {
  readonly code: typeof INVITE_EMAIL_RATE_LIMIT_CODE;
  readonly message: string;
};

/** Marker prefix on legacy error messages (pre-ConvexError deploy). */
export const INVITE_EMAIL_RATE_LIMIT_PREFIX = "INVITE_RATE_LIMIT:";

/** True when an error message originates from
 *  `assertInviteEmailCanSendOrThrow` and should be presented as a hint
 *  rather than an error in the UI. */
export function isInviteEmailRateLimitMessage(message: string): boolean {
  return message.includes(INVITE_EMAIL_RATE_LIMIT_PREFIX);
}

export function isInviteEmailRateLimitConvexData(
  data: unknown,
): data is InviteEmailRateLimitConvexData {
  return (
    data !== null &&
    typeof data === "object" &&
    (data as InviteEmailRateLimitConvexData).code ===
      INVITE_EMAIL_RATE_LIMIT_CODE &&
    typeof (data as InviteEmailRateLimitConvexData).message === "string"
  );
}

/**
 * Rest-Text, den Convex zurücklässt, wenn die eigentliche Meldung NICHT beim
 * Client ankam: In Prod schickt Convex nur `ConvexError.data` an den Browser —
 * die Message eines plain `Error` wird redigiert, übrig bleibt „Server Error"
 * plus der Stack-Marker „Called by client". Diese Reste sind keine Meldung und
 * dürfen nie in der UI landen.
 */
const FRAMEWORK_PLACEHOLDER_MESSAGES: readonly string[] = [
  "Called by client",
  "Server Error",
  "Uncaught Error",
  "Uncaught ConvexError",
];

export function isFrameworkPlaceholderMessage(message: string): boolean {
  const trimmed = message.trim().replace(/[.:]$/, "");
  return FRAMEWORK_PLACEHOLDER_MESSAGES.some(
    (placeholder) => trimmed.toLowerCase() === placeholder.toLowerCase(),
  );
}

export type InviteEmailClientErrorKind = "rate_limit" | "error";

export type InviteEmailClientErrorClassification = {
  readonly kind: InviteEmailClientErrorKind;
  readonly message: string;
};

/**
 * Maps action/mutation failures from invite send/resend into either a soft
 * rate-limit hint or a normal user-facing error line.
 */
export function classifyInviteEmailClientError(
  err: unknown,
): InviteEmailClientErrorClassification {
  if (
    err instanceof ConvexError &&
    isInviteEmailRateLimitConvexData(err.data)
  ) {
    return { kind: "rate_limit", message: err.data.message.trim() };
  }

  if (err instanceof ConvexError && typeof err.data === "string") {
    const data = hideInternalTerms(err.data.trim());
    if (data.length > 0) return { kind: "error", message: data };
  }

  const raw: string =
    err instanceof Error
      ? stripConvexClientErrorWrapper(err.message)
      : typeof err === "string"
        ? stripConvexClientErrorWrapper(err)
        : "";

  if (!raw || isFrameworkPlaceholderMessage(raw)) {
    return {
      kind: "error",
      message:
        "Die Aktion ist fehlgeschlagen. Bitte versuche es erneut oder kontaktiere den Support.",
    };
  }
  if (isInviteEmailRateLimitMessage(raw)) {
    return {
      kind: "rate_limit",
      message: stripInviteEmailRateLimitPrefix(raw),
    };
  }
  const legacy = raw.match(/INVITE_RATE_LIMIT:\s*(.+?)(?:\s+at\s+|$)/);
  if (legacy?.[1]) {
    return { kind: "rate_limit", message: legacy[1].trim() };
  }
  return { kind: "error", message: raw };
}

/**
 * Hides internal infrastructure terms from any user-facing text.
 * Single global rule: the word "convex" is never shown to end users.
 */
export function hideInternalTerms(text: string): string {
  return text.replace(/\bconvex\b/gi, "System");
}

function stripConvexClientErrorWrapperRaw(message: string): string {
  const trimmed = message.trim();
  const uncaughtMatch = /Uncaught (?:Error|ConvexError):/.exec(trimmed);
  const uncaughtIdx = uncaughtMatch?.index ?? -1;
  if (uncaughtIdx >= 0) {
    let rest = trimmed
      .slice(uncaughtIdx + (uncaughtMatch?.[0].length ?? 0))
      .trim();
    // `at fn (file:line)` or bare `at fn` / multiline stack after the German copy.
    const atParen = rest.search(/\s+at\s+[^(]+\([^)]+\)/);
    if (atParen > 0) {
      rest = rest.slice(0, atParen).trim();
    } else {
      const atBare = rest.search(/\s+at\s+[A-Za-z_$][\w$]*/);
      if (atBare > 0) rest = rest.slice(0, atBare).trim();
    }
    return rest.replace(/^Error:\s*/i, "").trim();
  }
  if (trimmed.startsWith("[CONVEX")) {
    const serverIdx = trimmed.indexOf("Server Error");
    if (serverIdx >= 0) {
      return stripConvexClientErrorWrapperRaw(
        trimmed.slice(serverIdx + "Server Error".length).trim(),
      );
    }
  }
  return trimmed.replace(/^Error:\s*/i, "").trim();
}

/** Strips Convex action wrapper text from client-side Error.message. */
export function stripConvexClientErrorWrapper(message: string): string {
  return hideInternalTerms(stripConvexClientErrorWrapperRaw(message));
}

/** Strip the marker prefix when surfacing the message to the UI. */
export function stripInviteEmailRateLimitPrefix(message: string): string {
  return message.startsWith(INVITE_EMAIL_RATE_LIMIT_PREFIX)
    ? message.slice(INVITE_EMAIL_RATE_LIMIT_PREFIX.length).trimStart()
    : message;
}

export type InviteEmailRateFields = {
  invite_email_last_sent_at?: number;
  invite_email_sent_day_start?: number;
  invite_email_sent_day_count?: number;
};

export function getEffectiveInviteEmailWindow(
  user: InviteEmailRateFields,
  now: number,
): { effectiveDayStart: number; effectiveDayCount: number } {
  const dayStart = user.invite_email_sent_day_start ?? 0;
  const dayCount = user.invite_email_sent_day_count ?? 0;
  if (dayStart === 0 || now - dayStart >= WINDOW_MS) {
    return { effectiveDayStart: now, effectiveDayCount: 0 };
  }
  return { effectiveDayStart: dayStart, effectiveDayCount: dayCount };
}

function throwInviteEmailRateLimit(message: string): never {
  const payload: InviteEmailRateLimitConvexData = {
    code: INVITE_EMAIL_RATE_LIMIT_CODE,
    message,
  };
  throw new ConvexError(payload);
}

export function assertInviteEmailCanSendOrThrow(
  user: InviteEmailRateFields,
  now: number,
): void {
  const { effectiveDayCount } = getEffectiveInviteEmailWindow(user, now);
  if (effectiveDayCount >= INVITE_EMAIL_RESEND_MAX_PER_24H) {
    throwInviteEmailRateLimit(
      `Maximal ${INVITE_EMAIL_RESEND_MAX_PER_24H} Einladungs-E-Mails an diese Adresse in 24 Stunden sind erlaubt. Bitte später erneut versuchen.`,
    );
  }
  const lastSent = user.invite_email_last_sent_at ?? 0;
  if (
    INVITE_EMAIL_RESEND_COOLDOWN_MS > 0 &&
    lastSent > 0 &&
    now - lastSent < INVITE_EMAIL_RESEND_COOLDOWN_MS
  ) {
    const waitSec = Math.max(
      1,
      Math.ceil((INVITE_EMAIL_RESEND_COOLDOWN_MS - (now - lastSent)) / 1000),
    );
    throwInviteEmailRateLimit(
      waitSec < 60
        ? `Bitte noch etwa ${waitSec} Sekunden warten, bevor du die Einladung erneut sendest.`
        : `Bitte noch etwa ${Math.ceil(waitSec / 60)} Minuten warten, bevor du die Einladung erneut sendest.`,
    );
  }
}

export function buildInviteEmailRecordAfterSend(
  user: InviteEmailRateFields,
  now: number,
): {
  invite_email_last_sent_at: number;
  invite_email_sent_day_start: number;
  invite_email_sent_day_count: number;
} {
  const { effectiveDayStart, effectiveDayCount } =
    getEffectiveInviteEmailWindow(user, now);
  return {
    invite_email_last_sent_at: now,
    invite_email_sent_day_start: effectiveDayStart,
    invite_email_sent_day_count: effectiveDayCount + 1,
  };
}

export function describeInviteEmailResendUiState(
  user: InviteEmailRateFields,
  now: number,
): { canSend: boolean; disabledReason: string | null } {
  const { effectiveDayStart, effectiveDayCount } =
    getEffectiveInviteEmailWindow(user, now);
  if (effectiveDayCount >= INVITE_EMAIL_RESEND_MAX_PER_24H) {
    const resetAt = effectiveDayStart + WINDOW_MS;
    const hoursLeft = Math.max(
      1,
      Math.ceil((resetAt - now) / (60 * 60 * 1000)),
    );
    return {
      canSend: false,
      disabledReason: `E-Mail-Limit erreicht. In etwa ${hoursLeft} Stunden erneut versuchen.`,
    };
  }
  const lastSent = user.invite_email_last_sent_at ?? 0;
  if (
    INVITE_EMAIL_RESEND_COOLDOWN_MS > 0 &&
    lastSent > 0 &&
    now - lastSent < INVITE_EMAIL_RESEND_COOLDOWN_MS
  ) {
    const waitSec = Math.max(
      1,
      Math.ceil((INVITE_EMAIL_RESEND_COOLDOWN_MS - (now - lastSent)) / 1000),
    );
    return {
      canSend: false,
      disabledReason:
        waitSec < 60
          ? `Bitte noch etwa ${waitSec} Sek. warten.`
          : `Bitte noch etwa ${Math.ceil(waitSec / 60)} Min. warten.`,
    };
  }
  return { canSend: true, disabledReason: null };
}
